import React, { useState, useEffect, useRef } from "react";
import {
  ShoppingCart,
  Search,
  Trash2,
  CreditCard,
  QrCode,
  Banknote,
  Receipt,
  Plus,
  Minus,
  Sparkles,
  Calculator,
  User,
  ArrowRight
} from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { formatCurrency } from "../../utils/formatters";
import { useNotification } from "../../app/context/NotificationContext";
import { ReceiptModal } from "./ReceiptModal";

export function RetailPOS() {
  const { products, batches, customers, processSale } = useApp();
  const { addToast } = useNotification();
  const searchInputRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(customers[0]?.id || "CUST-R04");
  const [paymentMethod, setPaymentMethod] = useState("UPI (GooglePay)");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [lastCompletedOrder, setLastCompletedOrder] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);

  // Keyboard shortcut: Press "/" to instantly focus search bar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "/" && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Filter products for POS quick scan grid
  const filteredProducts = React.useMemo(() => {
    if (!searchQuery.trim()) return products.slice(0, 16);
    const q = searchQuery.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.barcode.includes(q)
    );
  }, [products, searchQuery]);

  const addToCart = (product) => {
    const existingIdx = cart.findIndex((i) => i.productId === product.id);
    if (existingIdx !== -1) {
      const updated = [...cart];
      updated[existingIdx].qty += 1;
      updated[existingIdx].total = updated[existingIdx].qty * updated[existingIdx].sellingPrice;
      updated[existingIdx].profit = (updated[existingIdx].sellingPrice - updated[existingIdx].costPrice) * updated[existingIdx].qty;
      setCart(updated);
    } else {
      setCart([
        ...cart,
        {
          productId: product.id,
          productName: product.name,
          unit: product.unit,
          qty: 1,
          costPrice: product.purchasePrice,
          sellingPrice: product.retailPrice,
          mrp: product.MRP,
          total: product.retailPrice,
          profit: product.retailPrice - product.purchasePrice
        }
      ]);
    }
  };

  const updateCartQty = (index, delta) => {
    const updated = [...cart];
    const newQty = updated[index].qty + delta;
    if (newQty <= 0) {
      setCart(cart.filter((_, i) => i !== index));
      return;
    }
    updated[index].qty = newQty;
    updated[index].total = newQty * updated[index].sellingPrice;
    updated[index].profit = (updated[index].sellingPrice - updated[index].costPrice) * newQty;
    setCart(updated);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // Cart Totals & Live Margin Calculations
  const subtotal = cart.reduce((sum, i) => sum + i.total, 0);
  const totalCost = cart.reduce((sum, i) => sum + i.costPrice * i.qty, 0);
  const taxGst = Math.round(subtotal * 0.05); // 5% GST approximation
  const grandTotal = Math.max(0, subtotal + taxGst - Number(discountAmount || 0));
  const totalProfit = grandTotal - totalCost;
  const marginPercentage = grandTotal > 0 ? Number(((totalProfit / grandTotal) * 100).toFixed(2)) : 0;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      addToast("Your cart is empty. Please add items to scan.", "warning");
      return;
    }

    const customer = customers.find((c) => c.id === selectedCustomerId);

    try {
      const order = await processSale({
        channel: "Retail",
        customerId: selectedCustomerId,
        customerName: customer?.name || "Walk-in Retail Shopper",
        cashierName: "Anand Deshmukh (Retail Staff)",
        paymentMethod,
        paymentStatus: "Paid",
        items: cart,
        subtotal,
        taxGst,
        discountAmount: Number(discountAmount || 0),
        grandTotal,
        totalProfit,
        marginPercentage
      });

      setLastCompletedOrder(order);
      setIsReceiptOpen(true);
      setCart([]);
      setDiscountAmount(0);
      addToast(`Sale recorded! Bill Total: ${formatCurrency(grandTotal)}`, "success");
    } catch (err) {
      addToast("Failed to process sale.", "error");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      {/* Left: Instant Product Search & Quick Grid (7 Cols) */}
      <div className="lg:col-span-7 space-y-4">
        {/* Fast Scanner Search Bar */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Scan barcode or search products (Press '/' to focus)..."
            className="w-full pl-10 pr-20 py-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
            Press /
          </span>
        </div>

        {/* Quick Item Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 max-h-[600px] overflow-y-auto p-1">
          {filteredProducts.map((prod) => (
            <button
              key={prod.id}
              onClick={() => addToCart(prod)}
              className="p-3 bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl hover:border-emerald-500/60 dark:hover:border-emerald-500/60 hover:shadow-md transition text-left flex flex-col justify-between group"
            >
              <div>
                <span className="text-[10px] text-slate-400 font-mono block truncate">{prod.category}</span>
                <p className="font-bold text-slate-900 dark:text-white text-xs line-clamp-2 mt-0.5 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">
                  {prod.name}
                </p>
              </div>

              <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <span className="font-mono font-bold text-xs text-slate-900 dark:text-white">
                  {formatCurrency(prod.retailPrice)}
                </span>
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/60 px-1.5 py-0.5 rounded">
                  + Add
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Active POS Cart, Customer, Payment & Live Margin (5 Cols) */}
      <div className="lg:col-span-5 rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-xl space-y-4 text-xs">
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-500" />
            <h3 className="font-bold text-slate-900 dark:text-white text-sm font-display">
              Active Counter Cart ({cart.length})
            </h3>
          </div>
          {cart.length > 0 && (
            <button
              onClick={() => setCart([])}
              className="text-[11px] text-rose-500 hover:underline font-semibold"
            >
              Clear Cart
            </button>
          )}
        </div>

        {/* Customer Account Selector */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Customer Account / Loyalty Member
          </label>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs"
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.type} • {c.rating || "Standard"})
              </option>
            ))}
          </select>
        </div>

        {/* Cart Item Rows */}
        <div className="space-y-2 max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {cart.length === 0 ? (
            <div className="py-10 text-center text-slate-400">
              <ShoppingCart className="w-8 h-8 mx-auto mb-1 opacity-30" />
              <p>Cart is currently empty. Click or scan products on the left.</p>
            </div>
          ) : (
            cart.map((item, idx) => (
              <div key={idx} className="pt-2 flex items-center justify-between gap-2">
                <div className="flex-1 truncate">
                  <p className="font-bold text-slate-900 dark:text-white text-xs truncate">
                    {item.productName}
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono">
                    ₹{item.sellingPrice} x {item.qty} = {formatCurrency(item.total)}
                  </p>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => updateCartQty(idx, -1)}
                    className="p-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="w-6 text-center font-bold font-mono">{item.qty}</span>
                  <button
                    onClick={() => updateCartQty(idx, 1)}
                    className="p-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => removeFromCart(idx)}
                    className="p-1 text-slate-400 hover:text-rose-500 ml-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Live Margin Preview Box */}
        {cart.length > 0 && (
          <div className="p-3 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 rounded-2xl flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-semibold">
              <Calculator className="w-4 h-4" />
              <span>Real-Time Profit Margin:</span>
            </div>
            <span className="font-bold font-mono text-emerald-600 dark:text-emerald-400">
              +{formatCurrency(totalProfit)} ({marginPercentage}%)
            </span>
          </div>
        )}

        {/* Totals & Discounts */}
        <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
          <div className="flex justify-between text-slate-500">
            <span>Subtotal:</span>
            <span className="font-mono">{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>GST Tax (approx 5%):</span>
            <span className="font-mono">{formatCurrency(taxGst)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Discount (₹):</span>
            <input
              type="number"
              min="0"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(Number(e.target.value))}
              className="w-20 px-2 py-0.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded text-right font-mono"
            />
          </div>
          <div className="flex justify-between text-base font-extrabold text-slate-900 dark:text-white pt-2 border-t border-slate-200 dark:border-slate-800 font-display">
            <span>Grand Total:</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-mono">
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="space-y-2 pt-2">
          <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300">
            Payment Settlement Method
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "UPI (GPay / PhonePe)", val: "UPI (GooglePay)", icon: QrCode },
              { label: "Cash Drawer", val: "Cash", icon: Banknote },
              { label: "Debit / Credit Card", val: "Card (POS Swipe)", icon: CreditCard },
              { label: "Monthly Account Ledger", val: "Monthly Account Ledger", icon: User }
            ].map((method) => (
              <button
                key={method.val}
                type="button"
                onClick={() => setPaymentMethod(method.val)}
                className={`p-2 rounded-xl text-left border text-[11px] font-semibold flex items-center gap-1.5 transition ${
                  paymentMethod === method.val
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-600/20"
                    : "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100"
                }`}
              >
                <method.icon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{method.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Checkout Button */}
        <button
          onClick={handleCheckout}
          disabled={cart.length === 0}
          className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-2xl shadow-xl shadow-emerald-600/30 transition flex items-center justify-center gap-2 text-xs"
        >
          <span>Complete Sale & Print Thermal Receipt</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Printable Receipt Modal */}
      <ReceiptModal
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        order={lastCompletedOrder}
      />
    </div>
  );
}
